import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLocation } from 'react-router-dom';

// Generate or get visitor ID (persists across sessions)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('visitor_id');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitor_id', visitorId);
  }
  return visitorId;
};

// Generate or get session ID (unique per browser session)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) return 'Tablet';
  if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) return 'Mobile';
  return 'Desktop';
};

// Get browser name
const getBrowser = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('firefox')) return 'Firefox';
  if (userAgent.includes('edg')) return 'Edge';
  if (userAgent.includes('chrome')) return 'Chrome';
  if (userAgent.includes('safari')) return 'Safari';
  if (userAgent.includes('opera')) return 'Opera';
  return 'Unknown';
};

// Get country from browser language (fallback)
const getCountry = (): string => {
  const language = navigator.language || 'en-US';
  return language.split('-')[1] || 'Unknown';
};

// Get referrer
const getReferrer = (): string => {
  return document.referrer || 'Direct';
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const currentPageId = useRef<string | null>(null);

  useEffect(() => {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    const deviceType = getDeviceType();
    const browser = getBrowser();
    const country = getCountry();
    const referrer = getReferrer();
    const pagePath = location.pathname;

    // Record page visit
    const recordPageVisit = async () => {
      try {
        const { data, error } = await supabase
          .from('website_analytics')
          .insert({
            visitor_id: visitorId,
            session_id: sessionId,
            page_path: pagePath,
            device_type: deviceType,
            browser: browser,
            country: country,
            referrer: referrer,
            visited_at: new Date().toISOString(),
            time_on_page: 0,
          })
          .select('id')
          .single();

        if (error) {
          console.error('Error recording page visit:', error);
          return;
        }

        currentPageId.current = data?.id;
        pageStartTime.current = Date.now();
      } catch (err) {
        console.error('Error in visitor tracking:', err);
      }
    };

    recordPageVisit();

    // Update time on page when leaving
    return () => {
      if (currentPageId.current) {
        const timeOnPage = Math.floor((Date.now() - pageStartTime.current) / 1000);
        
        // Update time on page (fire and forget)
        supabase
          .from('website_analytics')
          .update({ time_on_page: timeOnPage })
          .eq('id', currentPageId.current)
          .then(() => {
            console.log('Time on page updated:', timeOnPage);
          });
      }
    };
  }, [location.pathname]);
};
